import { useEffect, useEffectEvent, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { createTapRouter, pushOpenUrl, type SettledTap } from '@/lib/pushTap'

// ============================================================================
// A tapped push alert opens its page, on an iPhone too (RIZ132, Task 11).
//
// WebKit ignores the url a tap asks for, so the service worker (public/push-sw.js)
// no longer moves a window: it leaves a note in Cache Storage, then focuses the
// open window and posts it { type: 'rizeos:push-open', url }, or opens a new one
// (at the app's start URL, on an iPhone). This hook is the page routing itself,
// through createTapRouter (src/lib/pushTap.ts, where each rule is tested):
//   - from the message, routed at once, before Cache Storage is touched;
//   - from the note, read on mount (the app opened by the tap) and whenever the
//     page comes back on screen, and read again 0.5, 1.5 and 3 s after each: an
//     iPhone can bring the app forward before the worker has written the note,
//     and may not deliver the message at all. A note written later than that
//     waits for the next mount or return to the screen.
// A message and a note for one tap route it once, and a page already at the
// tap's page is not pushed again. Every wait on Cache Storage gives up after
// 500 ms, so one that never answers holds no tap up. The bell's ?n= arrival
// then marks the row read and drops the parameter.
//
// MOUNTED ONCE, in AppLayout: every signed-in page renders inside it, and the
// sign-in, MFA and training pages do not, so a tap that lands while nobody is
// signed in waits as a note until someone is (two minutes at most).
// The page only reads and deletes the note. The worker alone writes it.
// ============================================================================

/** The page's address now: pathname + search, as a note's url is compared on. */
function currentAddress(): string {
  return window.location.pathname + window.location.search
}

// The tap this page last routed to, or found already open. Module scope, shared by every router the hook makes:
// the message and the note for one tap can arrive together (an iPhone coming forward), and whichever is second
// must see what the first did, across a remount of the layout too.
const memory: { settled: SettledTap | null } = { settled: null }

/** The service-worker container, or null where the page has none (a Firefox private window, an insecure page) or reading it throws. */
function serviceWorkerContainer(): ServiceWorkerContainer | null {
  try {
    return navigator.serviceWorker ?? null
  } catch (err) {
    console.warn('usePushTapRouting: the service worker cannot be reached here:', err)
    return null
  }
}

/** Cache Storage, or null where the page has none (an insecure page, an old Safari) or reading it throws (site data blocked). */
function cacheStorage(): CacheStorage | null {
  try {
    return window.caches ?? null
  } catch (err) {
    console.warn('usePushTapRouting: Cache Storage cannot be reached here:', err)
    return null
  }
}

export function usePushTapRouting(): void {
  const navigate = useNavigate()
  // The address this layout first rendered at, before the bell's ?n= arrival can replace it: the page a tap
  // opened, when the browser honoured the tap's url (Chromium does).
  const location = useLocation()
  const [landed] = useState(() => location.pathname + location.search)
  // navigate changes with the path, and the router below is made once per mount, so it reaches the current one
  // through an effect event.
  const route = useEffectEvent((url: string): void => {
    navigate(url)
  })

  useEffect(() => {
    const router = createTapRouter({
      storage: cacheStorage(),
      timers: { set: (run, ms) => setTimeout(run, ms), clear: (handle) => clearTimeout(handle) },
      now: () => Date.now(),
      here: () => ({ current: currentAddress(), landed }),
      route: (url) => route(url),
      memory,
      log: console,
    })
    const onMessage = (event: MessageEvent): void => {
      const url = pushOpenUrl(event.data)
      if (url !== null) void router.message(url)
    }
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') router.check()
    }
    const container = serviceWorkerContainer()
    container?.addEventListener('message', onMessage)
    document.addEventListener('visibilitychange', onVisible)
    router.check()
    return () => {
      router.stop()
      container?.removeEventListener('message', onMessage)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [landed])
}
